import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, Loader2, Home as HomeIcon, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PLACE_TYPE_LABELS } from '../lib/listingConstants';

const API = process.env.REACT_APP_BACKEND_URL;

const SavedCard = ({ item, onRemove }) => {
  const { listing_id, listing } = item;
  if (!listing) {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <p className="text-sm text-slate-500">This home is no longer available.</p>
          <Button variant="ghost" size="sm" onClick={() => onRemove(listing_id)} className="mt-2">Remove</Button>
        </CardContent>
      </Card>
    );
  }
  const photo = (listing.photos || [])[0];
  const src = photo?.signed_token ? `${API}/api/files/${photo.signed_token}` : null;
  return (
    <Link
      to={`/listings/${listing_id}`}
      className="block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-soft"
      data-testid={`saved-card-${listing_id}`}
    >
      <div className="relative aspect-[4/3] bg-slate-100">
        {src ? (
          <img src={src} alt={listing.title} className="w-full h-full object-cover" />
        ) : (
          <div className="h-full flex items-center justify-center"><HomeIcon className="h-10 w-10 text-slate-300" /></div>
        )}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); onRemove(listing_id); }}
          className="absolute top-2 right-2 h-9 w-9 rounded-full bg-white/90 shadow-sm flex items-center justify-center text-rose-600"
          data-testid={`saved-remove-${listing_id}`}
        >
          <Heart className="h-4 w-4 fill-current" />
        </button>
        <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-white/90 rounded-full text-[10px] font-bold">
          <ShieldCheck className="h-3 w-3 text-emerald-700" /> Verified
        </div>
      </div>
      <div className="p-3 space-y-1">
        <h3 className="font-heading font-semibold text-sm text-slate-900 line-clamp-1">{listing.title || 'Untitled home'}</h3>
        <p className="text-xs text-slate-500">
          {PLACE_TYPE_LABELS[listing.place_type] || ''}
          {listing.city ? ` · ${listing.city}` : ''}
        </p>
        <div className="pt-1">
          <span className="text-base font-heading font-extrabold text-slate-900">${(listing.total_monthly_cost_cad || 0).toLocaleString()}</span>
          <span className="text-xs text-slate-500">/mo</span>
          <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 bg-amber-500 text-white rounded-full">ALL-IN</span>
        </div>
      </div>
    </Link>
  );
};

const SavedListings = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/saved-listings');
      setItems(res.data.saved_listings || []);
    } catch (_) {
      toast.error("We couldn't load your saved homes.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onRemove = async (listing_id) => {
    try {
      await api.delete(`/saved-listings/${listing_id}`);
      setItems((it) => it.filter((x) => x.listing_id !== listing_id));
      toast('Removed from your saved list.');
    } catch (_) {
      toast.error("Couldn't remove that home.");
    }
  };

  return (
    <div className="min-h-screen bg-background pb-10" data-testid="saved-page">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">Saved homes</h1>
        <p className="mt-1 text-sm text-slate-600 mb-5">
          The places you've tapped the heart on. Tap again to remove.
        </p>
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>
        ) : items.length === 0 ? (
          <Card className="bg-card-cream border-amber-200/60">
            <CardContent className="p-8 sm:p-10 text-center">
              <Heart className="h-10 w-10 mx-auto text-amber-400 mb-3" />
              <h2 className="text-xl font-heading font-extrabold text-slate-900">No saved homes yet.</h2>
              <p className="mt-2 text-sm text-slate-700 max-w-sm mx-auto">
                Tap the heart on any listing to keep track of it here.
              </p>
              <Button asChild className="mt-5 btn-primary">
                <Link to="/search" data-testid="saved-browse-cta">Browse homes</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="saved-grid">
            {items.map((it) => <SavedCard key={it.listing_id} item={it} onRemove={onRemove} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default SavedListings;
